import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import {
  X,
  Link2,
  User,
  Phone,
  IndianRupee,
  FileText,
  Copy,
  QrCode,
  MessageCircle,
  Loader2,
  Sparkles,
  CheckCircle,
  ArrowLeft,
  CreditCard,
  Building2,
  ExternalLink,
  Check,
  Download,
  Share2,
} from "lucide-react";
import {
  createCustomerPaymentLinkApi,
  getStudioUpiVpaAPI,
  createUpiQrPaymentAPI,
  confirmUpiQrPaymentAPI,
} from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { StudioUpiDetails, UpiQrPaymentResponse } from "../../../../Data/transaction.dto";
import { trackClarityEvent } from "../../../../analytics/ClarityAnalytics";
import { CLARITY_EVENTS } from "../../../../analytics/EventTypes";
import { showSuccessToast, showErrorToast } from "../../Utlis/Toast";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../../../organisms/Dashboard/quotationMasterListShared";
import {
  Field,
  FieldGrid,
  Label,
  Input,
  ErrorSpan,
  FormLabelIcon,
  FormGradientDefs,
  gradient,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const PLATFORM_FEE = 10;

const initialFormData = {
  firstName: "",
  lastName: "",
  contact: "",
  amount: "",
  description: "",
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 14px;
  color: #171717;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const ReqStar = styled.span`
  color: #e44862;
  margin-left: 2px;
`;

const FeeCard = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.1) 0%, rgba(206, 86, 176, 0.08) 100%);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.68);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FeeTotal = styled.div`
  margin-top: 2px;
  font-family: Urbanist, sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #c94daa;
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const OptionCard = styled.div<{ $selected?: boolean; $tone?: "emerald" | "purple" }>`
  position: relative;
  border-radius: 16px;
  border: 2px solid
    ${(p) =>
      p.$selected
        ? p.$tone === "emerald"
          ? "#31a66a"
          : "#c94daa"
        : "rgba(23, 23, 23, 0.1)"};
  background: ${(p) =>
    p.$selected
      ? p.$tone === "emerald"
        ? "rgba(49, 166, 106, 0.04)"
        : "rgba(201, 77, 170, 0.04)"
      : "#ffffff"};
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(p) =>
    p.$selected
      ? p.$tone === "emerald"
        ? "0 6px 20px rgba(49, 166, 106, 0.12)"
        : "0 6px 20px rgba(201, 77, 170, 0.12)"
      : "0 2px 6px rgba(0, 0, 0, 0.03)"};

  &:hover {
    border-color: ${(p) =>
      p.$tone === "emerald" ? "#31a66a" : "#c94daa"};
    transform: translateY(-1px);
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const OptionTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OptionIcon = styled.div<{ $tone: "emerald" | "purple" }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.12)" : "rgba(201, 77, 170, 0.12)")};
  color: ${(p) => (p.$tone === "emerald" ? "#31a66a" : "#c94daa")};
`;

const OptionTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const OptionBadge = styled.span<{ $tone: "emerald" | "purple" }>`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  background: ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.15)" : "rgba(201, 77, 170, 0.15)")};
  color: ${(p) => (p.$tone === "emerald" ? "#227b4e" : "#a8358b")};
`;

const OptionDesc = styled.p`
  margin: 0 0 10px;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.45;
`;

const OptionAmountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px dashed rgba(23, 23, 23, 0.1);
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  color: rgba(23, 23, 23, 0.7);

  strong {
    font-family: Urbanist, sans-serif;
    font-size: 16px;
    font-weight: 800;
    color: #171717;
  }
`;

const UpiVpaBox = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(23, 23, 23, 0.03);
  border: 1px solid rgba(23, 23, 23, 0.08);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(23, 23, 23, 0.8);
`;

const HeroLeftWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const HeaderBackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
    background: #fff;
  }
`;

const SuccessBanner = styled.div<{ $tone?: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.08)" : "rgba(201, 77, 170, 0.08)")};
  border: 1px solid ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.2)" : "rgba(201, 77, 170, 0.2)")};
`;

const SuccessIconWrap = styled.div<{ $tone?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.15)" : "rgba(201, 77, 170, 0.15)")};
  color: ${(p) => (p.$tone === "emerald" ? "#31a66a" : "#c94daa")};
`;

const SuccessText = styled.div`
  min-width: 0;
`;

const SuccessTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const SuccessSub = styled.div`
  margin-top: 2px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const LinkBox = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fafafa;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #171717;
  word-break: break-all;
  line-height: 1.45;
`;

const ActionGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols || 3}, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ActionBtn = styled.button<{ $tone?: string; $solid?: boolean }>`
  min-height: 42px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$solid ? "transparent" : "rgba(23, 23, 23, 0.12)")};
  background: ${(p) => (p.$solid ? (p.$tone || "#25d366") : "#fff")};
  color: ${(p) => (p.$solid ? "#fff" : "rgba(23, 23, 23, 0.72)")};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-decoration: none;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$solid ? `0 6px 16px ${p.$tone || "#25d366"}40` : "none")};

  &:hover {
    transform: translateY(-1px);
    border-color: ${(p) => (p.$solid ? "transparent" : "rgba(201, 77, 170, 0.25)")};
    color: ${(p) => (p.$solid ? "#fff" : "#c94daa")};
  }
`;

const QrWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border-radius: 16px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const UtrConfirmCard = styled.div`
  padding: 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(203, 213, 225, 0.8);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UtrHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #1e293b;
`;

const UtrHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.4;
`;

const UtrInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const ConfirmPaidBtn = styled.button`
  height: 42px;
  padding: 0 18px;
  min-width: 140px;
  flex-shrink: 0;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 4px 14px rgba(201, 77, 170, 0.25);
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 500px) {
    width: 100%;
  }
`;

type ModalStep = "details" | "choose_method" | "result_cashfree" | "result_upi";

interface PaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const PaymentLinkModal = ({ isOpen, onClose, onSuccess }: PaymentLinkModalProps) => {
  const [step, setStep] = useState<ModalStep>("details");
  const [formData, setFormData] = useState(initialFormData);
  const [selectedMethod, setSelectedMethod] = useState<"upi_direct" | "cashfree">("upi_direct");

  // Cashfree state
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);

  // Direct UPI state
  const [upiDetails, setUpiDetails] = useState<StudioUpiDetails | null>(null);
  const [upiPaymentData, setUpiPaymentData] = useState<UpiQrPaymentResponse | null>(null);

  // UTR Confirmation state
  const [utrNumber, setUtrNumber] = useState("");
  const [confirmingUtr, setConfirmingUtr] = useState(false);
  const [utrSuccess, setUtrSuccess] = useState(false);

  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedImage, setCopiedImage] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      fetchStudioUpiDetails();
    }
  }, [isOpen]);

  const fetchStudioUpiDetails = async () => {
    try {
      const res = await getStudioUpiVpaAPI();
      if (res && res.data) {
        setUpiDetails(res.data);
      }
    } catch {
      // ignore
    }
  };

  const handleClose = () => {
    setStep("details");
    setFormData(initialFormData);
    setPaymentLink(null);
    setUpiPaymentData(null);
    setShowQR(false);
    setCopied(false);
    setError("");
    setUtrNumber("");
    setUtrSuccess(false);
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const baseAmount = Number(formData.amount) || 0;
  const cashfreeCustomerPays = baseAmount > 0 ? baseAmount + PLATFORM_FEE : 0;

  const validateDetails = () => {
    if (!formData.firstName.trim()) return "First Name is required";
    if (!formData.lastName.trim()) return "Last Name is required";
    if (!formData.contact.trim()) return "Contact Number is required";
    if (!/^\d+$/.test(formData.contact)) return "Contact must be a valid number";
    if (!formData.amount.trim()) return "Amount is required";
    if (Number(formData.amount) <= 0) return "Amount must be greater than 0";
    if (Number(formData.amount) > 100000) return "Amount cannot exceed ₹1,00,000 for UPI";
    if (!formData.description.trim()) return "Description is required";
    return "";
  };

  const handleProceedToMethod = () => {
    const vError = validateDetails();
    if (vError) {
      setError(vError);
      return;
    }
    setError("");
    setStep("choose_method");
  };

  // 1. Generate Direct UPI QR
  const handleGenerateDirectUpi = async () => {
    setError("");
    if (!upiDetails?.has_upi_vpa) {
      setError("Studio UPI ID is not configured. Please set your UPI ID in the Bank Account section on the Payments page.");
      return;
    }

    setLoading(true);
    try {
      const res = await createUpiQrPaymentAPI({
        amount: Number(baseAmount.toFixed(2)),
        note: formData.description.slice(0, 50),
        customer_name: `${formData.firstName} ${formData.lastName}`.trim(),
        contact_details: formData.contact.trim(),
      });

      if (res && res.upi_link) {
        setUpiPaymentData(res);
        setStep("result_upi");
        trackClarityEvent(CLARITY_EVENTS.CUSTOMER_PAYMENT_LINK_GENERATED, {
          amount: Number(baseAmount.toFixed(2)),
          payment_method: "upi_direct",
          customer_name: `${formData.firstName} ${formData.lastName}`,
          contact_details: formData.contact,
        });
        onSuccess?.();
      } else if (res?.code === "upi_vpa_missing") {
        setError("Studio UPI ID is not configured. Please set your UPI ID in the Bank Account section on the Payments page.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to generate UPI QR. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 2. Generate Cashfree Payment Link
  const handleGenerateCashfree = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await createCustomerPaymentLinkApi({
        first_name: formData.firstName,
        last_name: formData.lastName,
        contact_details: formData.contact,
        description: formData.description,
        amount: Number(cashfreeCustomerPays.toFixed(2)),
        host_name: window.location.origin,
        payment_provider: "cashfree",
      });

      if (res && res.short_url) {
        setPaymentLink(res.short_url);
        setStep("result_cashfree");
        trackClarityEvent(CLARITY_EVENTS.CUSTOMER_PAYMENT_LINK_GENERATED, {
          amount: Number(cashfreeCustomerPays.toFixed(2)),
          payment_method: "cashfree",
          customer_name: `${formData.firstName} ${formData.lastName}`,
          contact_details: formData.contact,
        });
        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to generate payment link. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Confirm UPI Payment with UTR
  const handleConfirmUpiPayment = async () => {
    if (!upiPaymentData?.payment_id) return;
    if (!utrNumber.trim()) {
      showErrorToast("Please enter the 12-digit UTR / UPI reference number.");
      return;
    }
    const cleanUtr = utrNumber.replace(/\s+/g, "");
    if (cleanUtr.length < 12 || cleanUtr.length > 35) {
      showErrorToast("UTR number must be at least 12 digits.");
      return;
    }

    setConfirmingUtr(true);
    try {
      const res = await confirmUpiQrPaymentAPI(upiPaymentData.payment_id, {
        status: "paid",
        utr: cleanUtr,
      });

      if (res && res.status === "paid") {
        setUpiPaymentData({
          ...upiPaymentData,
          status: "paid",
          utr: cleanUtr,
          payment_confirmed: true,
        });
        setUtrSuccess(true);
        showSuccessToast("Payment confirmed as PAID successfully!");
        onSuccess?.();
      }
    } catch {
      showErrorToast("Could not confirm payment.");
    } finally {
      setConfirmingUtr(false);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showSuccessToast("Copied to clipboard!");
    } catch {
      showSuccessToast("Could not copy");
    }
  };

  const getQrCardBlob = async (): Promise<Blob | null> => {
    const qrCanvas = document.getElementById("direct-upi-qrcode-canvas") as HTMLCanvasElement | null;
    if (!qrCanvas) return null;

    const cardCanvas = document.createElement("canvas");
    const ctx = cardCanvas.getContext("2d");
    if (!ctx) return null;

    const width = 420;
    const height = 430;
    cardCanvas.width = width;
    cardCanvas.height = height;

    // Card background
    ctx.fillStyle = "#ffffff";
    if (typeof (ctx as any).roundRect === "function") {
      ctx.beginPath();
      (ctx as any).roundRect(0, 0, width, height, 20);
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, width, height);
    }

    // Card border
    ctx.strokeStyle = "rgba(23, 23, 23, 0.08)";
    ctx.lineWidth = 2;
    if (typeof (ctx as any).roundRect === "function") {
      ctx.beginPath();
      (ctx as any).roundRect(0, 0, width, height, 20);
      ctx.stroke();
    }

    // Gradient bar at the top
    const grad = ctx.createLinearGradient(0, 0, width, 0);
    grad.addColorStop(0, "#ffc951");
    grad.addColorStop(1, "#ce56b0");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, 8);

    // Title: Amount
    ctx.fillStyle = "#171717";
    ctx.font = "bold 24px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.textAlign = "center";
    const amountVal = upiPaymentData?.amount ? Number(upiPaymentData.amount).toFixed(2) : "0.00";
    ctx.fillText(`Scan to Pay ₹${amountVal}`, width / 2, 48);

    // QR container box
    const qrSize = 250;
    const qrX = (width - qrSize) / 2;
    const qrY = 74;

    ctx.fillStyle = "#fafafa";
    if (typeof (ctx as any).roundRect === "function") {
      ctx.beginPath();
      (ctx as any).roundRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20, 16);
      ctx.fill();
      ctx.strokeStyle = "rgba(0, 0, 0, 0.06)";
      ctx.stroke();
    } else {
      ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
    }

    // Draw the QR canvas
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    // UPI ID
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.fillText(`UPI ID: ${upiPaymentData?.payee_vpa || ""}`, width / 2, qrY + qrSize + 36);

    // Reference ID
    ctx.fillStyle = "#64748b";
    ctx.font = "500 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.fillText(`Ref: ${upiPaymentData?.reference_id || ""}`, width / 2, qrY + qrSize + 58);

    return new Promise((resolve) => {
      cardCanvas.toBlob((blob) => resolve(blob), "image/png");
    });
  };

  const handleDownloadQr = async () => {
    try {
      const blob = await getQrCardBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const amountStr = upiPaymentData?.amount ? Number(upiPaymentData.amount).toFixed(0) : "0";
      a.download = `UPI_QR_₹${amountStr}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccessToast("QR code image downloaded!");
    } catch (err) {
      console.error(err);
      showErrorToast("Could not download QR image.");
    }
  };

  const handleCopyQrImage = async () => {
    try {
      const blob = await getQrCardBlob();
      if (!blob) return;
      if (navigator.clipboard && (window as any).ClipboardItem) {
        await navigator.clipboard.write([
          new (window as any).ClipboardItem({ "image/png": blob }),
        ]);
        setCopiedImage(true);
        setTimeout(() => setCopiedImage(false), 2500);
        showSuccessToast("QR image copied! Paste (Ctrl+V) directly to share.");
      } else {
        handleDownloadQr();
      }
    } catch (err) {
      console.error(err);
      handleDownloadQr();
    }
  };

  const handleShareDirectUpiQr = async () => {
    if (!upiPaymentData) return;

    // Try Web Share with File first (supported on mobile devices, Chrome, Safari)
    try {
      const blob = await getQrCardBlob();
      if (blob) {
        const file = new File(
          [blob],
          `UPI_QR_₹${Number(upiPaymentData.amount).toFixed(0)}.png`,
          { type: "image/png" }
        );

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "UPI Payment QR",
            text: `Please scan this QR code to pay ₹${Number(upiPaymentData.amount).toFixed(2)} directly via UPI (${upiPaymentData.payee_vpa}).`,
            files: [file],
          });
          return;
        }
      }
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
    }

    // Fallback for desktop: Copy to clipboard and open WhatsApp if phone is available
    try {
      const blob = await getQrCardBlob();
      if (blob && navigator.clipboard && (window as any).ClipboardItem) {
        await navigator.clipboard.write([
          new (window as any).ClipboardItem({ "image/png": blob }),
        ]);
        showSuccessToast("QR image copied to clipboard! Paste (Ctrl+V) to share.");
      }
    } catch {
      // Ignore
    }

    const phone = formData.contact.replace(/\D/g, "");
    if (phone) {
      const msg = `Hello ${formData.firstName || ""}, please scan the UPI QR code to pay ₹${Number(upiPaymentData.amount).toFixed(2)} directly via UPI (${upiPaymentData.payee_vpa}).\n\n(QR image has been copied to your clipboard — press Paste / Ctrl+V in this chat!)`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    }
  };

  const shareCashfreeWhatsApp = () => {
    if (paymentLink) {
      const message = `Pay ₹${cashfreeCustomerPays.toFixed(2)} to ${formData.firstName} ${formData.lastName}. Click here: ${paymentLink}`;
      window.open(`https://wa.me/${formData.contact.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`, "_blank");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={handleClose}>
        <ModalShell style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroLeftWrap>
                {step === "choose_method" && (
                  <HeaderBackBtn
                    type="button"
                    onClick={() => setStep("details")}
                    aria-label="Back to details"
                    disabled={loading}
                  >
                    <ArrowLeft size={18} />
                  </HeaderBackBtn>
                )}
                <HeroText>
                  <HeroBadge>
                    <Sparkles size={11} />
                    {step === "details"
                      ? "New Customer Payment"
                      : step === "choose_method"
                      ? "Payment Options"
                      : step === "result_upi"
                      ? "Direct UPI QR"
                      : "Payment Link Ready"}
                  </HeroBadge>
                  <ModalTitle>
                    {step === "details"
                      ? "New Customer Payment"
                      : step === "choose_method"
                      ? "Choose Payment Method"
                      : step === "result_upi"
                      ? "Direct UPI QR Ready"
                      : "Payment Link Ready"}
                  </ModalTitle>
                  <ModalSubtitle>
                    {step === "details"
                      ? "Fill in customer and payment details to generate link or QR."
                      : step === "choose_method"
                      ? `Select how you'd like to collect ₹${baseAmount.toFixed(2)} from ${formData.firstName} ${formData.lastName}.`
                      : step === "result_upi"
                      ? "Customer can scan the QR code to pay directly to your UPI ID."
                      : "Share the link with your customer to collect payment online."}
                  </ModalSubtitle>
                </HeroText>
              </HeroLeftWrap>
              <CloseBtn type="button" onClick={handleClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            {error && <ErrorSpan style={{ display: "block", marginBottom: 12 }}>{error}</ErrorSpan>}

            {/* STEP 1: FILL DETAILS */}
            {step === "details" && (
              <FormCard>
                <FieldGrid>
                  <Field>
                    <Label htmlFor="firstName">
                      <FormLabelIcon icon={User} />
                      First Name <ReqStar>*</ReqStar>
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="lastName">
                      <FormLabelIcon icon={User} />
                      Last Name <ReqStar>*</ReqStar>
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </Field>
                </FieldGrid>

                <Field>
                  <Label htmlFor="contact">
                    <FormLabelIcon icon={Phone} />
                    Contact Number <ReqStar>*</ReqStar>
                  </Label>
                  <Input
                    id="contact"
                    name="contact"
                    placeholder="Contact number"
                    value={formData.contact}
                    onChange={handleChange}
                  />
                </Field>

                <Field>
                  <Label htmlFor="amount">
                    <FormLabelIcon icon={IndianRupee} />
                    Amount (₹) <ReqStar>*</ReqStar>
                  </Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    placeholder="Amount (e.g. 500)"
                    value={formData.amount}
                    onChange={handleChange}
                  />
                </Field>

                <Field>
                  <Label htmlFor="description">
                    <FormLabelIcon icon={FileText} />
                    Description <ReqStar>*</ReqStar>
                  </Label>
                  <TextArea
                    id="description"
                    name="description"
                    placeholder="Payment description (e.g. Advance for wedding shoot)"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Field>
              </FormCard>
            )}

            {/* STEP 2: CHOOSE PAYMENT METHOD */}
            {step === "choose_method" && (
              <OptionsGrid>
                {/* OPTION 1: DIRECT UPI QR */}
                <OptionCard
                  $selected={selectedMethod === "upi_direct"}
                  $tone="purple"
                  onClick={() => setSelectedMethod("upi_direct")}
                >
                  <OptionHeader>
                    <OptionTitleWrap>
                      <OptionIcon $tone="purple">
                        <QrCode size={20} />
                      </OptionIcon>
                      <div>
                        <OptionTitle>Direct UPI QR</OptionTitle>
                      </div>
                    </OptionTitleWrap>
                    <OptionBadge $tone="purple">Direct to Bank</OptionBadge>
                  </OptionHeader>

                  <OptionDesc>
                    Customer scans with Google Pay, PhonePe, Paytm, BHIM, Cred, etc. Money settles
                    instantly in your bank account.
                  </OptionDesc>

                  {upiDetails?.has_upi_vpa ? (
                    <UpiVpaBox>
                      <div>
                        Receiving on: <strong>{upiDetails.upi_vpa}</strong>
                      </div>
                    </UpiVpaBox>
                  ) : (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 12px",
                        borderRadius: 10,
                        background: "rgba(228, 72, 98, 0.06)",
                        border: "1px solid rgba(228, 72, 98, 0.2)",
                        fontSize: 12,
                        color: "#be123c",
                        lineHeight: 1.4,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Studio UPI ID is not configured. Please set your UPI ID in the Bank Account section above before selecting this option.
                    </div>
                  )}

                  <OptionAmountRow>
                    <span>Platform Fee: ₹0.00</span>
                    <div>
                      Customer Pays: <strong>₹{baseAmount.toFixed(2)}</strong>
                    </div>
                  </OptionAmountRow>
                </OptionCard>

                {/* OPTION 2: CASHFREE GATEWAY */}
                <OptionCard
                  $selected={selectedMethod === "cashfree"}
                  $tone="purple"
                  onClick={() => setSelectedMethod("cashfree")}
                >
                  <OptionHeader>
                    <OptionTitleWrap>
                      <OptionIcon $tone="purple">
                        <CreditCard size={20} />
                      </OptionIcon>
                      <div>
                        <OptionTitle>Cashfree Payment Gateway</OptionTitle>
                      </div>
                    </OptionTitleWrap>
                    <OptionBadge $tone="purple">Auto-Verified</OptionBadge>
                  </OptionHeader>

                  <OptionDesc>
                    Hosted payment link supporting Credit Cards, Debit Cards, Netbanking & UPI with
                    automated status confirmation and webhook callbacks.
                  </OptionDesc>

                  <OptionAmountRow>
                    <span>Platform Fee: ₹{PLATFORM_FEE}</span>
                    <div>
                      Customer Pays: <strong>₹{cashfreeCustomerPays.toFixed(2)}</strong>
                    </div>
                  </OptionAmountRow>
                </OptionCard>
              </OptionsGrid>
            )}

            {/* STEP 3A: RESULT DIRECT UPI QR */}
            {step === "result_upi" && upiPaymentData && (
              <FormCard style={{ gap: 14 }}>
                <SuccessBanner $tone="purple">
                  <SuccessIconWrap $tone="purple">
                    <CheckCircle size={22} />
                  </SuccessIconWrap>
                  <SuccessText>
                    <SuccessTitle>Direct UPI QR Generated</SuccessTitle>
                    <SuccessSub>
                      Amount: ₹{Number(upiPaymentData.amount).toFixed(2)} 
                    </SuccessSub>
                  </SuccessText>
                </SuccessBanner>

                <QrWrap>
                  <QRCodeCanvas
                    id="direct-upi-qrcode-canvas"
                    value={upiPaymentData.upi_link}
                    size={220}
                    level="H"
                    includeMargin
                  />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#171717" }}>
                      Scan with any UPI app to pay
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                      UPI ID: <strong>{upiPaymentData.payee_vpa}</strong>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                      Ref ID: {upiPaymentData.reference_id}
                    </div>
                  </div>
                </QrWrap>

                <ActionGrid $cols={3}>
                  <ActionBtn type="button" onClick={handleShareDirectUpiQr}>
                    <Share2 size={15} />
                    Share QR
                  </ActionBtn>
                  <ActionBtn type="button" onClick={handleDownloadQr}>
                    <Download size={15} />
                    Download QR
                  </ActionBtn>
                  <ActionBtn type="button" onClick={handleCopyQrImage}>
                    {copiedImage ? <CheckCircle size={15} /> : <Copy size={15} />}
                    {copiedImage ? "Copied" : "Copy Image"}
                  </ActionBtn>
                </ActionGrid>

                {/* UTR Confirmation Section */}
                <UtrConfirmCard>
                  <UtrHeader>
                    <span>Reconciliation & Payment Verification</span>
                    {(upiPaymentData.payment_confirmed || utrSuccess) && (
                      <span style={{ color: "#31a66a", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <CheckCircle size={14} /> Confirmed Paid
                      </span>
                    )}
                  </UtrHeader>

                  {upiPaymentData.payment_confirmed || utrSuccess ? (
                    <div style={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>
                      ✓ Payment marked as PAID via UTR: <strong>{upiPaymentData.utr || utrNumber}</strong>
                    </div>
                  ) : (
                    <>
                      <UtrHint>
                        When the customer pays, verify the received funds in your bank SMS / UPI app,
                        then enter the customer's 12-digit UTR reference to confirm the payment.
                      </UtrHint>
                      <UtrInputRow>
                        <Input
                          placeholder="Enter 12-digit UTR (e.g. 412356789012)"
                          value={utrNumber}
                          onChange={(e) => setUtrNumber(e.target.value)}
                          style={{ flex: 1, minWidth: 0, height: 42, fontSize: 13 }}
                          disabled={confirmingUtr}
                        />
                        <ConfirmPaidBtn
                          type="button"
                          onClick={handleConfirmUpiPayment}
                          disabled={confirmingUtr || utrNumber.trim().replace(/\s+/g, "").length < 12}
                        >
                          {confirmingUtr ? <Spinner size={15} /> : <Check size={15} />}
                          {confirmingUtr ? "Confirming..." : "Confirm Paid"}
                        </ConfirmPaidBtn>
                      </UtrInputRow>
                      {utrNumber.trim().length > 0 && utrNumber.trim().replace(/\s+/g, "").length < 12 && (
                        <div style={{ fontSize: 11, color: "#e11d48", marginTop: -4, fontWeight: 500 }}>
                          UTR number must be at least 12 digits ({utrNumber.trim().replace(/\s+/g, "").length}/12)
                        </div>
                      )}
                    </>
                  )}
                </UtrConfirmCard>
              </FormCard>
            )}

            {/* STEP 3B: RESULT CASHFREE */}
            {step === "result_cashfree" && paymentLink && (
              <FormCard style={{ gap: 16 }}>
                <SuccessBanner>
                  <SuccessIconWrap>
                    <CheckCircle size={22} />
                  </SuccessIconWrap>
                  <SuccessText>
                    <SuccessTitle>Link generated successfully</SuccessTitle>
                    <SuccessSub>
                      Customer total: ₹{cashfreeCustomerPays.toFixed(2)} · {formData.firstName}{" "}
                      {formData.lastName}
                    </SuccessSub>
                  </SuccessText>
                </SuccessBanner>

                <Field>
                  <Label>
                    <FormLabelIcon icon={Link2} />
                    Payment Link
                  </Label>
                  <LinkBox>{paymentLink}</LinkBox>
                </Field>

                <ActionGrid>
                  <ActionBtn type="button" onClick={() => copyText(paymentLink)}>
                    {copied ? <CheckCircle size={15} /> : <Copy size={15} />}
                    {copied ? "Copied" : "Copy"}
                  </ActionBtn>
                  <ActionBtn type="button" onClick={() => setShowQR(!showQR)}>
                    <QrCode size={15} />
                    {showQR ? "Hide QR" : "Show QR"}
                  </ActionBtn>
                  <ActionBtn type="button" $tone="#25d366" $solid onClick={shareCashfreeWhatsApp}>
                    <MessageCircle size={15} />
                    WhatsApp
                  </ActionBtn>
                </ActionGrid>

                {showQR && (
                  <QrWrap>
                    <QRCodeCanvas value={paymentLink} size={200} />
                  </QrWrap>
                )}
              </FormCard>
            )}
          </ModalBody>

          <ModalActions>
            {step === "details" && (
              <>
                <SecondaryButton type="button" onClick={handleClose}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="button" onClick={handleProceedToMethod}>
                  <Link2 size={16} />
                  Generate Link
                </PrimaryButton>
              </>
            )}

            {step === "choose_method" && (
              <PrimaryButton
                type="button"
                onClick={selectedMethod === "upi_direct" ? handleGenerateDirectUpi : handleGenerateCashfree}
                disabled={loading}
                style={{ width: "100%" }}
              >
                {loading ? (
                  <Spinner size={16} />
                ) : selectedMethod === "upi_direct" ? (
                  <QrCode size={16} />
                ) : (
                  <Link2 size={16} />
                )}
                {loading
                  ? "Generating..."
                  : selectedMethod === "upi_direct"
                  ? "Generate UPI QR"
                  : "Generate Cashfree Link"}
              </PrimaryButton>
            )}

            {(step === "result_upi" || step === "result_cashfree") && (
              <PrimaryButton type="button" onClick={handleClose} style={{ width: "100%" }}>
                Done
              </PrimaryButton>
            )}
          </ModalActions>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};

export default PaymentLinkModal;
